import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, rootApi, SIGNED_OUT_EVENT, type Me } from "./api";

/** Who is signed in to Mivio (one login across every business they belong to). */
interface SessionState {
  me: Me | null;
  ready: boolean;
  refresh: () => Promise<void>;
  signedIn: (me: Me) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setMe(await rootApi<Me>("/me"));
    } catch (err) {
      if (err instanceof ApiError && err.status !== 401) console.warn(err.message);
      setMe(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onSignedOut = () => setMe(null);
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
  }, [refresh]);

  const signOut = useCallback(async () => {
    await rootApi("/auth/sign-out", { method: "POST" }).catch(() => undefined);
    setMe(null);
  }, []);

  const value = useMemo(() => ({ me, ready, refresh, signedIn: (m: Me) => setMe(m), signOut }), [me, ready, refresh, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "M";
}

/** A person's square: their initials (photos come with Account settings). */
export function Avatar({ name, className }: { name: string; photo?: string | null; className?: string }) {
  return <span className={`avatar${className ? ` ${className}` : ""}`} aria-hidden="true">{initials(name)}</span>;
}

/** A business's square: its first letters on a colour picked from its name. */
const MARKS = ["#36fba1", "#8bdcff", "#ffc96b", "#c8b1ff", "#ff9fb4", "#b6ef6b"];
export function BusinessMark({ name, small }: { name: string; small?: boolean }) {
  const colour = MARKS[[...name].reduce((n, c) => n + c.charCodeAt(0), 0) % MARKS.length];
  return <span className={`avatar avatar--business${small ? " avatar--sm" : ""}`} style={{ background: colour, color: "#0b0b0c" }} aria-hidden="true">{initials(name)}</span>;
}

/** Leaves the page for another part of Mivio (another business, your login): a full load, like switching stores. */
export function go(path: string): void {
  window.location.assign(path);
}
